import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Link,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Bookmark as SavedIcon,
  BookmarkBorder as SaveIcon,
  LocationOnOutlined as LocationIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { jobsApi } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { useExtensionStatus } from '../../hooks/useExtensionStatus';
import { prefillLinkProps, usePrefillOnOpen } from '../../hooks/usePrefillOnOpen';
import { useSaveJob } from '../../hooks/useSaveJob';
import { Job, JobApplicationState, JobDetail, statusMeta } from '../../types';
import { errorMessage } from '../../utils/errors';
import { formatDate } from '../../utils/format';
import {
  AGENCY_HINT,
  AGENCY_LABEL,
  atsLabel,
  categoryLabel,
  companyBoardSearch,
  externalUrl,
  jobTypeLabel,
  levelLabel,
  postedAgo,
  publishesApplicationForm,
  workplaceLabel,
} from '../../utils/jobBoard';
import ApplicationQuestions from './ApplicationQuestions';
import JobMatchSection from './JobMatchSection';

interface Props {
  jobId: string;
  /** The list item, shown at once while the full job loads. */
  preview?: Job | null;
  notify: (message: string, severity?: 'success' | 'error') => void;
  /** Lets the list behind a drawer reflect a save or un-save made here. */
  onApplicationChange?: (jobId: string, next: JobApplicationState) => void;
  /** h1 on the job's own page, h2 inside the drawer. */
  titleComponent?: 'h1' | 'h2';
  titleId?: string;
}

const externalLinkProps = { target: '_blank', rel: 'noopener noreferrer' } as const;

/**
 * One job in full: facts, description, Apply and Save. Shared by the drawer
 * over the board and the /jobs/:id page. Mount it with `key={jobId}` so its
 * state starts fresh for each job.
 */
const JobDetailView: React.FC<Props> = ({
  jobId,
  preview = null,
  notify,
  onApplicationChange,
  titleComponent = 'h2',
  titleId,
}) => {
  // Items carry the viewer's application state, so reload when they sign in or out.
  const viewer = useAuthStore((state) => (state.isAuthenticated ? state.user?.id ?? 'user' : ''));
  const signedIn = viewer !== '';
  // The preference is set on the board, which only shows it to signed-in users.
  const [prefillOnOpen] = usePrefillOnOpen();
  const prefill = signedIn ? prefillOnOpen : 'off';
  const extension = useExtensionStatus();
  const [detail, setDetail] = useState<JobDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  // A save made here wins over whatever a slower detail response says.
  const [override, setOverride] = useState<JobApplicationState | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    setNotFound(false);
    jobsApi
      .getJob(jobId, controller.signal)
      .then((data) => {
        setDetail(data);
        setLoading(false);
      })
      .catch((e) => {
        if (controller.signal.aborted) return;
        if ((e as { response?: { status?: number } })?.response?.status === 404) {
          setNotFound(true);
        } else {
          setError(errorMessage(e, 'Could not load this job.'));
        }
        setLoading(false);
      });
    return () => controller.abort();
  }, [jobId, reloadKey, viewer]);

  const handleChange = useCallback(
    (id: string, next: JobApplicationState) => {
      setOverride(next);
      onApplicationChange?.(id, next);
    },
    [onApplicationChange]
  );
  const { toggleSave, savingIds } = useSaveJob(notify, handleChange);

  const base: Job | null = detail ?? preview;

  if (!base) {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress aria-label="Loading job" />
        </Box>
      );
    }
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <Typography id={titleId} variant="h6" component={titleComponent} gutterBottom>
          {notFound ? 'This job is no longer listed' : 'Could not load this job'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {notFound ? 'It may have been filled or taken down by the employer.' : error}
        </Typography>
        <Stack direction="row" spacing={1} justifyContent="center">
          {!notFound && (
            <Button variant="contained" onClick={() => setReloadKey((key) => key + 1)}>
              Retry
            </Button>
          )}
          <Button component={RouterLink} to="/" variant="outlined">
            Browse all jobs
          </Button>
        </Stack>
      </Box>
    );
  }

  const job: Job = override ? { ...base, ...override } : base;
  const status = job.application_status;
  const meta = status ? statusMeta(status) : null;
  const saved = status === 'saved';
  const canToggleSave = !status || saved;
  const saving = savingIds.includes(job.id);
  const applyUrl = externalUrl(job.application_url) || externalUrl(job.source_url);
  const postingUrl = externalUrl(job.source_url) || applyUrl;
  // Only promise a prefill the extension can deliver: installed and on this account.
  const willPrefill =
    prefill !== 'off' && applyUrl !== '' && extension?.authenticated === true && extension.userId === viewer;
  const companySearch = companyBoardSearch(job.company.name, job.company.is_staffing_agency);
  const tech = job.tech_stack ?? [];
  const sectionHeading = titleComponent === 'h1' ? 'h2' : 'h3';

  const facts: { label: string; value: string }[] = [];
  if (job.salary_display) {
    const stated = job.salary_interval && job.salary_interval !== 'year' ? ` (stated per ${job.salary_interval})` : '';
    facts.push({ label: 'Salary', value: `${job.salary_display} a year${stated}` });
  }
  if (job.workplace_type) facts.push({ label: 'Workplace', value: workplaceLabel(job.workplace_type) });
  if (job.experience_level) facts.push({ label: 'Level', value: levelLabel(job.experience_level) });
  if (job.min_years_experience !== null && job.min_years_experience !== undefined) {
    facts.push({ label: 'Experience', value: `${job.min_years_experience}+ years` });
  }
  if (job.job_type) facts.push({ label: 'Job type', value: jobTypeLabel(job.job_type) });
  if (job.category) facts.push({ label: 'Category', value: categoryLabel(job.category) });
  if (job.department) facts.push({ label: 'Department', value: job.department });
  if (job.posted_at) {
    facts.push({ label: 'Posted', value: `${postedAgo(job.posted_at)} (${formatDate(job.posted_at)})` });
  }
  if (job.source_platform) facts.push({ label: 'Listed on', value: atsLabel(job.source_platform) });

  return (
    <Box>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
        <Avatar
          src={job.company.logo_url || undefined}
          alt=""
          variant="rounded"
          sx={{ width: 44, height: 44, bgcolor: 'grey.200', color: 'text.secondary' }}
        >
          {job.company.name?.[0]?.toUpperCase() ?? '?'}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 1, rowGap: 0.25 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3, minWidth: 0 }}>
              {job.company.name}
            </Typography>
            {job.company.is_staffing_agency && (
              <Tooltip title={AGENCY_HINT} describeChild>
                <Chip size="small" variant="outlined" label={AGENCY_LABEL} />
              </Tooltip>
            )}
          </Box>
          {companySearch && (
            <Link component={RouterLink} to={{ pathname: '/', search: companySearch }} variant="caption">
              All jobs at {job.company.name}
            </Link>
          )}
        </Box>
        {meta && (
          <Chip
            size="small"
            label={meta.label}
            aria-label={`Your application: ${meta.label}`}
            sx={{ bgcolor: meta.color, color: 'white', fontWeight: 600 }}
          />
        )}
      </Stack>

      <Typography
        id={titleId}
        variant="h5"
        component={titleComponent}
        sx={{ fontWeight: 700, lineHeight: 1.25, overflowWrap: 'anywhere', mb: 1 }}
      >
        {job.title}
      </Typography>

      <Stack direction="row" spacing={0.5} alignItems="flex-start" sx={{ mb: 2, color: 'text.secondary' }}>
        <LocationIcon sx={{ fontSize: 18, mt: '2px', flexShrink: 0 }} />
        <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
          {job.location || 'Location not listed'}
        </Typography>
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 1 }}>
        {applyUrl ? (
          <Button
            component="a"
            href={applyUrl}
            {...externalLinkProps}
            {...prefillLinkProps(prefill, applyUrl, { jobId: job.id, title: job.title, company: job.company.name })}
            variant="contained"
            size="large"
            endIcon={<OpenInNewIcon />}
            sx={{ fontWeight: 600, px: 4 }}
          >
            Apply
          </Button>
        ) : (
          <Button variant="contained" size="large" disabled>
            No apply link
          </Button>
        )}
        {canToggleSave ? (
          <Button
            variant="outlined"
            size="large"
            startIcon={saving ? <CircularProgress size={18} /> : saved ? <SavedIcon /> : <SaveIcon />}
            onClick={() => toggleSave(job)}
            disabled={saving || (!saved && detail?.is_active === false)}
            aria-pressed={saved}
            title={saved ? 'Remove from your saved jobs' : undefined}
          >
            {saved ? 'Saved' : 'Save'}
          </Button>
        ) : (
          job.application_id && (
            <Button component={RouterLink} to={`/applications/${job.application_id}`} variant="outlined" size="large">
              View your application
            </Button>
          )
        )}
      </Stack>
      {detail && detail.is_active === false ? (
        <Alert severity="warning" sx={{ mb: 2.5 }}>
          This listing is no longer on the employer&apos;s job board. The application form may be closed.
        </Alert>
      ) : (
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0, mb: 2.5 }}>
          Apply opens the employer&apos;s application form in a new tab.{' '}
          {willPrefill ? 'Will prefill when it opens.' : 'The JobFlo extension fills it in for you there.'}
        </Typography>
      )}

      {facts.length > 0 && (
        <Box
          component="dl"
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))' },
            gap: 2,
            m: 0,
            mb: 2.5,
          }}
        >
          {facts.map((fact) => (
            <Box key={fact.label}>
              <Typography component="dt" variant="caption" color="text.secondary">
                {fact.label}
              </Typography>
              <Typography component="dd" variant="body2" sx={{ m: 0, fontWeight: 500, overflowWrap: 'anywhere' }}>
                {fact.value}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {tech.length > 0 && (
        <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75, mb: 2.5 }} aria-label="Technologies">
          {tech.map((name) => (
            <Chip key={name} size="small" label={name} sx={{ bgcolor: 'grey.100' }} />
          ))}
        </Stack>
      )}

      {signedIn && (
        <>
          <Divider sx={{ mb: 2.5 }} />
          <JobMatchSection jobId={jobId} headingComponent={sectionHeading} />
        </>
      )}

      {/* Signed out, the API sends no questions; an ATS that does not
          publish the form (Lever) has nothing to read, so say nothing. */}
      {detail && signedIn && (
        detail.questions ? (
          <>
            <Divider sx={{ mb: 2.5 }} />
            <ApplicationQuestions questions={detail.questions} headingComponent={sectionHeading} />
          </>
        ) : (
          publishesApplicationForm(job.source_platform) && (
            <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0, mb: 2.5 }}>
              We haven&apos;t read this application&apos;s questions yet.
            </Typography>
          )
        )
      )}

      <Divider sx={{ mb: 2.5 }} />

      <Typography variant="subtitle1" component={sectionHeading} sx={{ fontWeight: 600, mb: 1 }}>
        Description
      </Typography>

      {detail ? (
        <>
          {!detail.description_is_full && (
            <Alert severity="info" sx={{ mb: 2 }}>
              The full description could not be loaded, so this is only the start of it.{' '}
              {postingUrl && (
                <Link href={postingUrl} {...externalLinkProps} color="inherit" sx={{ fontWeight: 600 }}>
                  Read the full posting
                </Link>
              )}
            </Alert>
          )}
          {/* Plain text from the API: rendered as text with its line breaks
              kept, never as HTML. */}
          <Typography
            variant="body2"
            component="div"
            sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', lineHeight: 1.7 }}
          >
            {detail.description || 'No description was provided for this job.'}
          </Typography>
        </>
      ) : loading ? (
        <Box aria-busy="true" aria-label="Loading description">
          {[96, 100, 92, 100, 85, 60].map((width, index) => (
            <Skeleton key={index} variant="text" width={`${width}%`} />
          ))}
        </Box>
      ) : (
        <Alert
          severity={notFound ? 'warning' : 'error'}
          action={
            notFound ? undefined : (
              <Button color="inherit" size="small" onClick={() => setReloadKey((key) => key + 1)}>
                Retry
              </Button>
            )
          }
        >
          {notFound ? 'This job is no longer listed.' : error}
        </Alert>
      )}

      {postingUrl && (
        <Box sx={{ mt: 3 }}>
          <Link href={postingUrl} {...externalLinkProps} variant="body2">
            View the original posting{job.source_platform ? ` on ${atsLabel(job.source_platform)}` : ''}
          </Link>
        </Box>
      )}
    </Box>
  );
};

export default JobDetailView;
